import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  TrendingUp,
  LocalShipping,
  CheckCircle,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom blue location pin for one-off collections
const blueIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMjU2M2ViIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Custom red location pin for repeat customers
const redIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjZWY0NDQ0Ij48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Custom green location pin for multiple collections
const greenIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMTBiOTgxIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

interface OverviewStats {
  today: {
    total_orders: number;
    completed_orders: number;
    pending_orders: number;
    total_revenue: number;
  };
  week: {
    total_orders: number;
    completed_orders: number;
    total_revenue: number;
  };
  month: {
    total_orders: number;
    completed_orders: number;
    total_revenue: number;
  };
}

interface OrderLocation {
  id: number;
  order_number: string;
  customer_name: string;
  delivery_address: string;
  delivery_postcode: string;
  latitude: number;
  longitude: number;
  collection_status: string;
  is_repeat_customer: boolean;
  is_multiple_collection: boolean;
}

interface Driver {
  id: number;
  name: string;
  driver_code: string;
  latitude: number;
  longitude: number;
  location_updated_at: string;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}> = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ height: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: '50%',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Overview: React.FC = () => {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [orderLocations, setOrderLocations] = useState<OrderLocation[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [todayTotalCollections, setTodayTotalCollections] = useState(0);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    try {
      // Get first and last day of current month
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      const startDate = firstDay.toISOString().split('T')[0];
      const endDate = lastDay.toISOString().split('T')[0];
      
      const [statsResponse, ordersResponse, driversResponse] = await Promise.all([
        api.get('/analytics/overview'),
        api.get('/orders?limit=1000'),
        api.get('/users/drivers/locations')
      ]);
      
      setStats(statsResponse.data);
      
      // Filter orders with valid coordinates and ensure they're from this month
      const locatedOrders = ordersResponse.data.orders
        .filter((order: any) => {
          // Check if order has coordinates
          if (!order.delivery_latitude || !order.delivery_longitude) return false;
          
          // Check the date is within this month using date_created field
          const orderDate = new Date(order.date_created);
          return orderDate >= firstDay && orderDate <= lastDay;
        })
        .map((order: any) => {
          // Check if it's a repeat customer (has previous orders)
          const isRepeatCustomer = order.previous_orders_count > 0;
          
          // Check if it's a multiple collection (more than 1 occasion for any service type)
          const isMultipleCollection = 
            (order.bins_number_of_occasions && order.bins_number_of_occasions > 1) ||
            (order.bags_number_of_occasions && order.bags_number_of_occasions > 1) ||
            (order.large_bins_number_of_occasions && order.large_bins_number_of_occasions > 1);
          
          return {
            id: order.id,
            order_number: order.order_number,
            customer_name: `${order.customer_first_name} ${order.customer_last_name}`,
            delivery_address: order.delivery_address_1,
            delivery_postcode: order.delivery_postcode,
            latitude: parseFloat(order.delivery_latitude),
            longitude: parseFloat(order.delivery_longitude),
            collection_status: order.collection_status || 'unassigned',
            is_repeat_customer: isRepeatCustomer,
            is_multiple_collection: isMultipleCollection
          };
        });
      
      setOrderLocations(locatedOrders);
      setDrivers(driversResponse.data);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch today's total collections for completion rate
    const fetchTodayCollections = async () => {
      try {
        const response = await api.get('/collection-events/today');
        const totalCollections = response.data.events?.length || 0;
        setTodayTotalCollections(totalCollections);
      } catch (error) {
        console.error('Error fetching today collections:', error);
      }
    };
    if (stats) {
      fetchTodayCollections();
    }
  }, [stats]);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!stats) {
    return <Typography>No data available</Typography>;
  }
  
  const completionRate = todayTotalCollections > 0
    ? Math.round((stats.today.completed_orders / todayTotalCollections) * 100)
    : 0;

  return (
    <Box>
      
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Today's New Orders"
            value={stats.today.total_orders}
            icon={<LocalShipping sx={{ color: 'white' }} />}
            color="#2563eb"
            subtitle={`${stats.today.pending_orders} pending`}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Collections Completed Today"
            value={stats.today.completed_orders}
            icon={<CheckCircle sx={{ color: 'white' }} />}
            color="#10b981"
            subtitle={`${completionRate}% completion rate`}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Today's Revenue"
            value={`£${parseFloat(String(stats.today.total_revenue || '0')).toFixed(2)}`}
            icon={<TrendingUp sx={{ color: 'white' }} />}
            color="#7c3aed"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              This Week's Performance
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">New Orders</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats.week.total_orders}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Completed Collections</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats.week.completed_orders}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Revenue</Typography>
                <Typography variant="body2" fontWeight="bold">
                  £{parseFloat(String(stats.week.total_revenue || '0')).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              This Month's Performance
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">New Orders</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats.month.total_orders}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Completed Collections</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats.month.completed_orders}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Revenue</Typography>
                <Typography variant="body2" fontWeight="bold">
                  £{parseFloat(String(stats.month.total_revenue || '0')).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              This Month's Order Locations
            </Typography>
            
            {/* Legend for map pins */}
            <Box display="flex" gap={3} mb={2} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#ef4444', borderRadius: '50%' }} />
                <Typography variant="body2">Repeat Customer</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#10b981', borderRadius: '50%' }} />
                <Typography variant="body2">Multiple Collections</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#2563eb', borderRadius: '50%' }} />
                <Typography variant="body2">One-off Collection</Typography>
              </Box>
            </Box>
            
            <Box sx={{ height: 400, width: '100%' }}>
              {orderLocations.length > 0 ? (
                <MapContainer
                  center={[53.4808, -2.2426]} // Manchester center
                  zoom={10}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  
                  {/* Collection location markers with color coding */}
                  {orderLocations.map((order) => {
                    // Determine which icon to use based on order type
                    // Red overrides all for repeat customers
                    let icon = blueIcon; // Default: one-off collection
                    let orderType = 'One-off Collection';
                    
                    if (order.is_repeat_customer) {
                      icon = redIcon;
                      orderType = 'Repeat Customer';
                    } else if (order.is_multiple_collection) {
                      icon = greenIcon;
                      orderType = 'Multiple Collections';
                    }
                    
                    return (
                      <Marker
                        key={`order-${order.id}`}
                        position={[order.latitude, order.longitude]}
                        icon={icon}
                      >
                        <Popup>
                          <div>
                            <strong>Order #{order.order_number}</strong><br />
                            Customer: {order.customer_name}<br />
                            Address: {order.delivery_address}<br />
                            Postcode: {order.delivery_postcode}<br />
                            Collection Status: <strong>{order.collection_status}</strong><br />
                            Type: <strong style={{ color: order.is_repeat_customer ? '#ef4444' : order.is_multiple_collection ? '#10b981' : '#2563eb' }}>
                              {orderType}
                            </strong>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              ) : (
                <Typography color="textSecondary" align="center" sx={{ pt: 10 }}>
                  No collection locations for this month
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Overview;